<template>
    <UIDialog v-if="isOpen" :open="isOpen" @update:open="closeModal">
        <DialogScrollContent :class="'max-w-5xl border-none p-0'">
            <DialogHeader class="px-6 pt-8">
                <DialogTitle>Search History</DialogTitle>
                <p class="text-sm text-muted-foreground">View and manage your previous searches on the platform.</p>
            </DialogHeader>

            <div class="mb-6 px-6">
                <!-- Tabs - Always visible -->
                <div class="mb-4 border-b">
                    <div class="-mb-px flex">
                        <button
                            class="px-4 py-2 text-sm font-medium"
                            :class="
                                currentTab === 'previous'
                                    ? 'border-b-2 border-primary text-primary'
                                    : 'text-muted-foreground'
                            "
                            :disabled="isLoadingTab"
                            @click="switchTab('previous')"
                        >
                            Previous Searches
                            <span class="ml-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-xs">{{
                                previousSearches.pagination?.total || 0
                            }}</span>
                        </button>
                        <button
                            class="px-4 py-2 text-sm font-medium"
                            :class="
                                currentTab === 'saved'
                                    ? 'border-b-2 border-primary text-primary'
                                    : 'text-muted-foreground'
                            "
                            :disabled="isLoadingTab"
                            @click="switchTab('saved')"
                        >
                            Saved Searches
                            <span class="ml-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-xs">{{
                                savedSearches.pagination?.total || 0
                            }}</span>
                        </button>
                    </div>
                </div>

                <!-- Previous Searches Tab -->
                <div v-if="currentTab === 'previous'" class="rounded-lg border">
                    <div
                        class="max-h-[50vh] overflow-x-auto overflow-y-auto scrollbar scrollbar-track-slate-200 scrollbar-thumb-slate-400 scrollbar-w-2 scrollbar-h-2 hover:scrollbar-thumb-slate-500 active:scrollbar-thumb-slate-600"
                    >
                        <table class="w-full text-sm">
                            <thead class="sticky top-0 z-10">
                                <tr class="border-b bg-slate-100">
                                    <th class="px-4 py-3 text-left font-medium">Date</th>
                                    <th class="px-4 py-3 text-left font-medium">Platform</th>
                                    <th class="px-4 py-3 text-left font-medium">Source</th>
                                    <th class="px-4 py-3 text-left font-medium">Filters</th>
                                    <th class="px-4 py-3 text-left font-medium">Results</th>
                                    <th class="px-4 py-3 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <!-- Skeleton loading state for Previous Searches -->
                                <template v-if="isLoading">
                                    <tr
                                        v-for="i in 10"
                                        :key="`skeleton-previous-${i}`"
                                        class="border-b last:border-b-0"
                                    >
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-24 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-20 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-20 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-16 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-24 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3 text-right">
                                            <div class="flex justify-end gap-2">
                                                <div class="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
                                                <div class="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>

                                <!-- Actual content when loaded -->
                                <template v-else>
                                    <tr
                                        v-for="search in previousSearches.data"
                                        :key="search._id ? search._id.$oid : search.request_hash"
                                        class="border-b last:border-b-0 hover:bg-muted/50"
                                    >
                                        <td class="px-4 py-3 align-top">{{ formatDate(search.ts) }}</td>
                                        <td class="px-4 py-3 align-top">
                                            {{ formatPlatform(search.request.params.platform_id) }}
                                        </td>
                                        <td class="px-4 py-3 align-top">
                                            <span class="whitespace-nowrap" :title="getSourceTitle(search)">
                                                {{ getSourceLabel(search) }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3 align-top">
                                            <SearchHistoryFilters :search="search" />
                                        </td>
                                        <td class="px-4 py-3 align-top">
                                            <span class="whitespace-nowrap">
                                                {{ formatResultCount(search) }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3 text-right align-top">
                                            <div class="flex justify-end gap-2">
                                                <Tooltip>
                                                    <template #trigger>
                                                        <button
                                                            :disabled="isSavingId || search.isSaved"
                                                            class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                                                            :class="{ 'bg-green-50 text-green-600': search.isSaved }"
                                                            @click="saveSearch(search)"
                                                        >
                                                            <AppIcon
                                                                :name="isSavingId === search._id
                                                                        ? 'spinner-third'
                                                                        : search.isSaved
                                                                          ? 'bookmark-slash'
                                                                          : 'bookmark'"
                                                                :class="isSavingId === search._id ? 'animate-spin' : ''"
                                                                class="h-4 w-4"
                                                            />
                                                        </button>
                                                    </template>
                                                    <template #default>
                                                        {{ search.isSaved ? 'Already saved' : 'Save search' }}
                                                    </template>
                                                </Tooltip>
                                                <Tooltip>
                                                    <template #trigger>
                                                        <button
                                                            class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                            @click="runSearch(search)"
                                                        >
                                                            <AppIcon
                                                                name="play"
                                                                class="h-4 w-4"
                                                            />
                                                        </button>
                                                    </template>
                                                    <template #default>Run search again</template>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="previousSearches.data.length === 0 && !isLoading">
                                        <td colspan="6" class="px-4 py-8 text-center text-muted-foreground">
                                            No search history found.
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination for Previous Searches -->
                    <div
                        v-if="previousSearches.pagination && previousSearches.pagination.pages > 1 && !isLoading"
                        class="flex items-center justify-between border-t p-2"
                    >
                        <div class="text-sm text-muted-foreground">
                            Showing
                            {{ (previousSearches.pagination.page - 1) * previousSearches.pagination.limit + 1 }} to
                            {{
                                Math.min(
                                    previousSearches.pagination.page * previousSearches.pagination.limit,
                                    previousSearches.pagination.total,
                                )
                            }}
                            of {{ previousSearches.pagination.total }} entries
                        </div>
                        <Pagination
                            v-slot="{ page }"
                            :items-per-page="previousSearches.pagination.limit"
                            :total="previousSearches.pagination.total"
                            :sibling-count="1"
                            show-edges
                            :default-page="previousSearches.pagination.page"
                            @update:page="changePage('previous', $event)"
                        >
                            <PaginationList v-slot="{ items }" class="flex items-center gap-1">
                                <PaginationPrev />
                                <template v-for="(item, index) in items">
                                    <PaginationListItem
                                        v-if="item.type === 'page'"
                                        :key="index"
                                        :value="item.value"
                                        as-child
                                    >
                                        <Button
                                            class="h-10 w-10 p-0"
                                            :variant="item.value === page ? 'default' : 'outline'"
                                        >
                                            {{ item.value }}
                                        </Button>
                                    </PaginationListItem>
                                    <PaginationEllipsis v-else :key="item.type" :index="index" />
                                </template>
                                <PaginationNext />
                            </PaginationList>
                        </Pagination>
                    </div>
                </div>

                <!-- Saved Searches Tab -->
                <div v-if="currentTab === 'saved'" class="rounded-lg border">
                    <div
                        class="max-h-96 overflow-x-auto overflow-y-auto scrollbar scrollbar-track-slate-200 scrollbar-thumb-slate-400 scrollbar-w-2 scrollbar-h-2 hover:scrollbar-thumb-slate-500 active:scrollbar-thumb-slate-600"
                    >
                        <table class="w-full text-sm">
                            <thead class="sticky top-0 z-10">
                                <tr class="border-b bg-slate-100">
                                    <th class="px-4 py-3 text-left font-medium">Name</th>
                                    <th class="px-4 py-3 text-left font-medium">Date Saved</th>
                                    <th class="px-4 py-3 text-left font-medium">Platform</th>
                                    <th class="px-4 py-3 text-left font-medium">Source</th>
                                    <th class="px-4 py-3 text-left font-medium">Filters</th>
                                    <th class="px-4 py-3 text-left font-medium">Results</th>
                                    <th class="px-4 py-3 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <!-- Skeleton loading state for Saved Searches -->
                                <template v-if="isLoading">
                                    <tr v-for="i in 5" :key="`skeleton-saved-${i}`" class="border-b last:border-b-0">
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-28 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-24 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-20 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-20 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-32 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <div class="h-5 w-24 animate-pulse rounded bg-slate-200"></div>
                                        </td>
                                        <td class="px-4 py-3 text-right">
                                            <div class="flex justify-end gap-2">
                                                <div class="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
                                                <div class="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
                                                <div class="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>

                                <!-- Actual content when loaded -->
                                <template v-else>
                                    <tr
                                        v-for="search in savedSearches.data"
                                        :key="search._id ? search._id.$oid : search.request_hash"
                                        class="border-b last:border-b-0 hover:bg-muted/50"
                                    >
                                        <td class="px-4 py-3 align-top">
                                            <div class="w-44">
                                                <div v-if="editingId === search._id" class="space-y-1.5">
                                                    <Input
                                                        v-model="editingName"
                                                        class="!h-auto !py-1 !px-2 text-sm"
                                                        placeholder="Enter search name"
                                                        maxlength="100"
                                                        @keyup.enter="updateSearchName(search)"
                                                        @keyup.escape="cancelEditing()"
                                                    />
                                                    <div class="flex justify-end gap-1.5">
                                                        <Button
                                                            variant="outline"
                                                            size="xs"
                                                            @click="cancelEditing()"
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            class="border border-transparent"
                                                            size="xs"
                                                            :disabled="isUpdatingId === search._id"
                                                            @click="updateSearchName(search)"
                                                        >
                                                            {{ isUpdatingId === search._id ? 'Saving...' : 'Save' }}
                                                        </Button>
                                                    </div>
                                                </div>
                                                <span v-else class="text-sm">
                                                    {{ search.name || 'Unknown' }}
                                                </span>
                                            </div>
                                        </td>
                                        <td class="px-4 py-3 align-top">{{ formatDate(search.ts) }}</td>
                                        <td class="px-4 py-3 align-top">
                                            {{ formatPlatform(search.request.params.platform_id) }}
                                        </td>
                                        <td class="px-4 py-3 align-top">
                                            <span class="whitespace-nowrap" :title="getSourceTitle(search)">
                                                {{ getSourceLabel(search) }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3 align-top">
                                            <SearchHistoryFilters :search="search" />
                                        </td>
                                        <td class="px-4 py-3 align-top">
                                            <span class="whitespace-nowrap">
                                                {{ formatResultCount(search) }}
                                            </span>
                                        </td>
                                        <td class="px-4 py-3 text-right align-top">
                                            <div class="flex justify-end gap-2">
                                                <Tooltip>
                                                    <template #trigger>
                                                        <button
                                                            class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                            @click="startEditing(search)"
                                                        >
                                                            <AppIcon
                                                                name="pen-to-square"
                                                                class="h-4 w-4"
                                                            />
                                                        </button>
                                                    </template>
                                                    <template #default>Edit name</template>
                                                </Tooltip>
                                                <Tooltip>
                                                    <template #trigger>
                                                        <button
                                                            :disabled="isDeletingId === search._id"
                                                            class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                                                            @click="removeFromSaved(search)"
                                                        >
                                                            <AppIcon
                                                                :name="isDeletingId === search._id
                                                                        ? 'spinner-third'
                                                                        : 'trash-alt'"
                                                                :class="
                                                                    isDeletingId === search._id ? 'animate-spin' : ''
                                                                "
                                                                class="h-4 w-4"
                                                            />
                                                        </button>
                                                    </template>
                                                    <template #default>Remove from saved</template>
                                                </Tooltip>
                                                <Tooltip>
                                                    <template #trigger>
                                                        <button
                                                            class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                            @click="runSearch(search)"
                                                        >
                                                            <AppIcon
                                                                name="play"
                                                                class="h-4 w-4"
                                                            />
                                                        </button>
                                                    </template>
                                                    <template #default>Run search again</template>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="savedSearches.data.length === 0 && !isLoading">
                                        <td colspan="7" class="px-4 py-8 text-center text-muted-foreground">
                                            No saved searches found. Save a search to see it here.
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination for Saved Searches -->
                    <div
                        v-if="savedSearches.pagination && savedSearches.pagination.pages > 1 && !isLoading"
                        class="flex items-center justify-between border-t p-2"
                    >
                        <div class="text-sm text-muted-foreground">
                            Showing
                            {{ (savedSearches.pagination.page - 1) * savedSearches.pagination.limit + 1 }} to
                            {{
                                Math.min(
                                    savedSearches.pagination.page * savedSearches.pagination.limit,
                                    savedSearches.pagination.total,
                                )
                            }}
                            of {{ savedSearches.pagination.total }} entries
                        </div>
                        <Pagination
                            v-slot="{ page }"
                            :items-per-page="savedSearches.pagination.limit"
                            :total="savedSearches.pagination.total"
                            :sibling-count="1"
                            show-edges
                            :default-page="savedSearches.pagination.page"
                            @update:page="changePage('saved', $event)"
                        >
                            <PaginationList v-slot="{ items }" class="flex items-center gap-1">
                                <PaginationPrev />
                                <template v-for="(item, index) in items">
                                    <PaginationListItem
                                        v-if="item.type === 'page'"
                                        :key="index"
                                        :value="item.value"
                                        as-child
                                    >
                                        <Button
                                            class="h-10 w-10 p-0"
                                            :variant="item.value === page ? 'default' : 'outline'"
                                        >
                                            {{ item.value }}
                                        </Button>
                                    </PaginationListItem>
                                    <PaginationEllipsis v-else :key="item.type" :index="index" />
                                </template>
                                <PaginationNext />
                            </PaginationList>
                        </Pagination>
                    </div>
                </div>
            </div>
        </DialogScrollContent>
    </UIDialog>
</template>

<script setup>
import { ref, reactive, watch, onMounted, getCurrentInstance } from 'vue'
import { axios } from '@/utils/axios'
import { Dialog as UIDialog, DialogHeader, DialogTitle, DialogScrollContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Pagination,
    PaginationList,
    PaginationListItem,
    PaginationEllipsis,
    PaginationNext,
    PaginationPrev,
} from '@/components/ui/pagination'
import Tooltip from '@/components/Tooltip.vue'
import SearchHistoryFilters from './SearchHistoryFilters.vue'

const instance = getCurrentInstance()

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    platformId: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['update:isOpen', 'runSearch'])

const currentTab = ref('previous')
const isSavingId = ref(null) // Track which search is being saved by ID
const isDeletingId = ref(null) // Track which search is being deleted by ID
const isUpdatingId = ref(null) // Track which search name is being updated
const isLoading = ref(true) // Start with loading state true
const editingId = ref(null) // Track which search is being edited
const editingName = ref('')
const isLoadingTab = ref(false) // Separate state for tab switching

// Pagination state
const previousSearches = reactive({
    data: [],
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0,
    },
})

const savedSearches = reactive({
    data: [],
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0,
    },
})

// Reset both pagination objects to page 1
const resetPagination = () => {
    previousSearches.pagination.page = 1
    savedSearches.pagination.page = 1
}

// Watch for the modal opening
watch(
    () => props.isOpen,
    async (newValue) => {
        if (newValue) {
            // Set loading immediately when modal opens
            isLoading.value = true
            resetPagination()
            // Fetch only the current tab's data
            await Promise.all([
                fetchTabData('previous'),
                fetchTabData('saved'),
            ])

        }
    },
    { immediate: false }, // This will run immediately on component mount
)

// Switch tabs function - now fetches only the selected tab's data
const switchTab = async (tab) => {
    if (tab === currentTab.value) return

    // Set loading state immediately when switching tabs
    isLoadingTab.value = true
    isLoading.value = true
    currentTab.value = tab

    // Only fetch data for the current tab
    await fetchTabData(tab)

    isLoadingTab.value = false
}

// Fetch data for a specific tab
const fetchTabData = async (tab) => {
    try {
        const currentPage = tab === 'previous' ? previousSearches.pagination.page : savedSearches.pagination.page

        const limit = tab === 'previous' ? previousSearches.pagination.limit : savedSearches.pagination.limit

        // Construct query parameters
        const params = {
            page: currentPage,
            limit: limit,
        }

        // Make API request to the appropriate endpoint based on tab
        const endpoint = tab === 'previous' ? '/discovery/history' : '/discovery/history/saved'

        const { data } = await axios.get(endpoint, { params })

        // Update only the relevant state
        if (tab === 'previous') {
            previousSearches.data = data.payload.data || []
            previousSearches.pagination = data.payload.pagination || {
                page: 1,
                limit: 10,
                total: previousSearches.data.length,
                pages: 1,
            }
        } else {
            savedSearches.data = data.payload.data || []
            savedSearches.pagination = data.payload.pagination || {
                page: 1,
                limit: 10,
                total: savedSearches.data.length,
                pages: 1,
            }
        }
    } catch (error) {
        console.error(`Error fetching ${tab} searches data`, error)
        if (tab === 'previous') {
            previousSearches.data = []
        } else {
            savedSearches.data = []
        }
    } finally {
        isLoading.value = false
    }
}

const refreshSavedSearches = () => fetchTabData('saved')

defineExpose({
    refreshSavedSearches,
})

const closeModal = () => {
    emit('update:isOpen', false)
}

const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown'

    try {
        const date = new Date(timestamp)
        return date.toLocaleString()
    } catch (error) {
        return timestamp
    }
}

const formatPlatform = (platformId) => {
    if (!platformId) return 'Unknown'
    if (platformId === 'twitter') return 'X'
    return platformId.charAt(0).toUpperCase() + platformId.slice(1)
}

const getSearchQuery = (search) => search?.request?.body?.query || {}

const getFriendlySourceName = (search) => (
    search?.source?.name ||
    search?.source_name ||
    search?.request?.source?.name ||
    search?.request?.body?.source?.name ||
    null
)

const getSourceLabel = (search) => {
    const friendlyName = getFriendlySourceName(search)
    if (friendlyName) return friendlyName

    const query = getSearchQuery(search)
    const listIds = Array.isArray(query.list_id) ? query.list_id : (query.list_id ? [query.list_id] : [])
    const campaignIds = Array.isArray(query.campaign_id) ? query.campaign_id : (query.campaign_id ? [query.campaign_id] : [])
    if (listIds.length && campaignIds.length) return 'Lists + Campaigns'
    if (listIds.length) return listIds.length > 1 ? 'Lists' : 'List'
    if (campaignIds.length) return campaignIds.length > 1 ? 'Campaigns' : 'Campaign'

    return 'All creators'
}

const getSourceTitle = (search) => {
    const friendlyName = getFriendlySourceName(search)
    if (friendlyName) return friendlyName

    const query = getSearchQuery(search)
    const listIds = Array.isArray(query.list_id) ? query.list_id : (query.list_id ? [query.list_id] : [])
    const campaignIds = Array.isArray(query.campaign_id) ? query.campaign_id : (query.campaign_id ? [query.campaign_id] : [])
    const sourceParts = []

    if (listIds.length) {
        sourceParts.push(listIds.length > 1 ? `Lists: ${listIds.join(', ')}` : `List: ${listIds[0]}`)
    }

    if (campaignIds.length) {
        sourceParts.push(campaignIds.length > 1 ? `Campaigns: ${campaignIds.join(', ')}` : `Campaign: ${campaignIds[0]}`)
    }

    if (sourceParts.length) {
        return sourceParts.join(' | ')
    }

    return 'All active creators'
}

const getCreatorCount = (search) => Number(search?.response?.creators || 0)

const formatResultCount = (search) => {
    const count = getCreatorCount(search)
    if (count >= 10000) return '10k+'
    return count.toLocaleString()
}

const startEditing = (search) => {
    editingId.value = search._id
    editingName.value = search.name || ''
}

const cancelEditing = () => {
    editingId.value = null
    editingName.value = ''
}

const updateSearchName = async (search) => {
    isUpdatingId.value = search._id

    try {
        await axios.put(`/discovery/history/saved/${search._id}`, {
            name: editingName.value,
        })

        // Update the name in the local data
        const index = savedSearches.data.findIndex((s) => s._id === search._id)
        if (index !== -1) {
            savedSearches.data[index].name = editingName.value
        }

        cancelEditing()

        instance.proxy.$successHandler('Search name updated successfully')
    } catch (error) {
        console.error('Error updating search name', error)
        instance.proxy.$errorHandler('Failed to update search name')
    } finally {
        isUpdatingId.value = null
    }
}

const saveSearch = async (search) => {
    // Set saving state with specific ID
    isSavingId.value = search._id

    try {
        const response = await axios.post(`/discovery/history/saved/${search._id}`, search)

        // If saved successfully, update the UI to reflect the saved state
        if (response.status === 200) {
            // Mark this search as saved in the UI
            const index = previousSearches.data.findIndex((s) => s._id === search._id)
            if (index !== -1) {
                previousSearches.data[index].isSaved = true
            }

            // Refresh saved searches tab data if it might affect the current view
            if (currentTab.value === 'saved') {
                await fetchTabData('saved')
            }

            instance.proxy.$successHandler('Search saved successfully')
        } else if (response.status === 409) {
            // If already saved, just update the UI
            const index = previousSearches.data.findIndex((s) => s._id === search._id)
            if (index !== -1) {
                previousSearches.data[index].isSaved = true
            }

            instance.proxy.$successHandler('Search is already saved')
        }
    } catch (error) {
        console.error('Error saving search', error)
        // Check if it's a 409 Conflict error (already saved)
        if (error.response && error.response.status === 409) {
            // Mark as saved anyway
            const index = previousSearches.data.findIndex((s) => s._id === search._id)
            if (index !== -1) {
                previousSearches.data[index].isSaved = true
            }
            instance.proxy.$successHandler('Search is already saved')
        } else {
            instance.proxy.$errorHandler('Failed to save search')
        }
    } finally {
        isSavingId.value = null
    }
}

const removeFromSaved = async (search) => {
    // Set the deleting state with specific ID
    isDeletingId.value = search._id

    try {
        await axios.delete(`/discovery/history/saved/${search._id}`)

        // After removing, refresh saved searches
        await fetchTabData('saved')

        // If we're also showing previous searches that might have a "saved" indicator,
        // update their saved status too
        if (currentTab.value === 'previous') {
            // Find any matching search in the previous tab and update its isSaved status
            const index = previousSearches.data.findIndex((s) => s.request_hash === search.request_hash)
            if (index !== -1) {
                previousSearches.data[index].isSaved = false
            } else {
                // If not visible in the current page, refresh the previous searches data
                // to make sure all saved statuses are current
                await fetchTabData('previous')
            }
        }

        instance.proxy.$successHandler('Saved search removed')
    } catch (error) {
        console.error('Error removing saved search', error)
        instance.proxy.$errorHandler('Failed to remove saved search')
    } finally {
        isDeletingId.value = null
    }
}

const runSearch = (search) => {
    closeModal()
    emit('runSearch', search)
}

const changePage = (type, page) => {
    if (type === 'previous') {
        previousSearches.pagination.page = page
    } else {
        savedSearches.pagination.page = page
    }

    isLoading.value = true
    // Only fetch data for the current tab
    fetchTabData(type)
}

// Initialize by setting loading state to true on component creation
onMounted(() => {
    isLoading.value = true
})
</script>
